import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CurrencyInputHelperFun } from '../../Constants/utils';
import {
  createChatbotPoProduct,
  fetchQuickAddCategoryId,
} from './chatbotPoCatalogApi';
import { formatCreatePoCostPerItem } from './poCreateLogic';
import styles from './AdminChatbot.module.scss';

const MAX_MONEY_DIGITS = 6;

const sanitizeMoneyInput = (raw, { allowEmpty = false } = {}) => {
  const digits = String(raw ?? '')
    .replace(/\D/g, '')
    .slice(0, MAX_MONEY_DIGITS);
  if (!digits) return allowEmpty ? '' : '0.00';
  return CurrencyInputHelperFun(digits);
};

const generateUpc = () => {
  let out = '';
  for (let i = 0; i < 12; i += 1) out += Math.floor(Math.random() * 10);
  return out;
};

/**
 * Compact Create-product-from-invoice modal.
 * Upstream: Quickadd_product_react/quick_add_product_new (same as AI create product).
 */
const ChatbotCreateProductFromInvoice = ({
  open,
  auth,
  merchantId,
  prefill,
  busy: parentBusy,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [upc, setUpc] = useState('');
  const [cost, setCost] = useState('0.00');
  const [sellingPrice, setSellingPrice] = useState('0.00');
  const [categoryId, setCategoryId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const seededOpenRef = useRef(false);

  // Prefill form fields once when the modal opens — do not re-run on parent
  // re-renders (e.g. chatbot resize recreating an inline `prefill` object).
  useEffect(() => {
    if (!open) {
      seededOpenRef.current = false;
      return;
    }
    if (seededOpenRef.current) return;
    seededOpenRef.current = true;
    setName(String(prefill?.name || '').trim());
    setUpc(String(prefill?.upc || '').trim());
    const costValue = formatCreatePoCostPerItem(
      prefill?.cost != null && prefill.cost !== ''
        ? Number(prefill.cost)
        : 0,
    );
    setCost(sanitizeMoneyInput(costValue));
    setSellingPrice(
      costValue === '0.00'
        ? ''
        : sanitizeMoneyInput(costValue, { allowEmpty: true }),
    );
    setError('');
  }, [open, prefill]);

  // Category lookup once per open/merchant — cached + deduped in catalog API.
  useEffect(() => {
    if (!open || !merchantId) return undefined;
    let cancelled = false;
    (async () => {
      const id = await fetchQuickAddCategoryId(merchantId);
      if (!cancelled) setCategoryId(id);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, merchantId]);

  if (!open) return null;

  const handleSave = async () => {
    const trimmedName = name.trim();
    const trimmedUpc = upc.trim();
    if (!trimmedName) {
      setError('Product name is required.');
      return;
    }
    if (!trimmedUpc) {
      setError('UPC is required.');
      return;
    }
    if (!categoryId) {
      setError('No Quickadd category found. Create a "Quickadd" category first.');
      return;
    }
    const sell = Number(sellingPrice === '' ? cost : sellingPrice);
    if (!Number.isFinite(sell) || sell < 0) {
      setError('Selling price must be zero or more.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const data = await createChatbotPoProduct(auth, {
        merchantId,
        name: trimmedName,
        upc: trimmedUpc,
        sellingPrice: sell,
        costPrice: cost,
        quantity: 0,
        categoryId,
        categoryName: 'Quickadd',
      });
      const productId =
        data?.product_id ||
        data?.id ||
        data?.result?.product_id ||
        data?.result?.id ||
        data?.data?.product_id ||
        data?.data?.id ||
        '';
      const ok = data?.status === true || data?.status === 'true' || Boolean(productId);
      if (!ok) {
        const reason =
          data?.update_message ||
          data?.message ||
          'Product could not be created. It may already exist.';
        setError(String(reason));
        return;
      }
      onCreated({
        productId: String(productId),
        title: trimmedName,
        upc: trimmedUpc,
        cost: formatCreatePoCostPerItem(Number(cost)),
        stock: 0,
        variantId: '0',
      });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to create product.');
    } finally {
      setSaving(false);
    }
  };

  const disabled = saving || parentBusy;

  // Portal onto the chatbot panel so the sheet is never clipped by the
  // tall PO review card / messages scrollport.
  const portalTarget =
    typeof document !== 'undefined'
      ? document.querySelector('[data-chatbot-panel="true"]')
      : null;

  const dialog = (
    <div
      className={styles.poCreateProductOverlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="chatbot-create-product-title"
    >
      <div className={styles.poCreateProductModal}>
        <header className={styles.poCreateProductHeader}>
          <h4 id="chatbot-create-product-title">Create new product</h4>
          <button
            type="button"
            onClick={onClose}
            disabled={disabled}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className={styles.poCreateProductBody}>
          <p className={styles.poCreateProductHint}>
            Quick Add to catalog, then bind this PO line.
          </p>
          <label className={styles.poField}>
            <span>Name</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              disabled={disabled}
            />
          </label>
          <label className={styles.poField}>
            <span>UPC</span>
            <div className={styles.poUpcRow}>
              <input
                value={upc}
                onChange={(event) => setUpc(event.target.value)}
                disabled={disabled}
              />
              <button
                type="button"
                className={styles.poSecondary}
                onClick={() => setUpc(generateUpc())}
                disabled={disabled || Boolean(String(upc).trim())}
              >
                Generate
              </button>
            </div>
          </label>
          <div className={styles.poGrid}>
            <label className={styles.poField}>
              <span>Cost</span>
              <div className={styles.poMoneyInput}>
                <em aria-hidden="true">$</em>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={cost}
                  onChange={(event) =>
                    setCost(sanitizeMoneyInput(event.target.value))
                  }
                  disabled={disabled}
                  aria-label="Cost"
                />
              </div>
            </label>
            <label className={styles.poField}>
              <span>Selling price</span>
              <div className={styles.poMoneyInput}>
                <em aria-hidden="true">$</em>
                <input
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  value={sellingPrice}
                  onChange={(event) =>
                    setSellingPrice(
                      sanitizeMoneyInput(event.target.value, {
                        allowEmpty: true,
                      }),
                    )
                  }
                  disabled={disabled}
                  placeholder="0.00"
                  aria-label="Selling price"
                />
              </div>
            </label>
          </div>
          {error ? (
            <p className={styles.poCreateProductError} role="alert">
              {error}
            </p>
          ) : null}
        </div>
        <div className={styles.poCreateProductActions}>
          <button
            type="button"
            className={styles.poGhost}
            onClick={onClose}
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.poPrimary}
            onClick={handleSave}
            disabled={disabled}
          >
            {saving ? 'Saving…' : 'Create & add'}
          </button>
        </div>
      </div>
    </div>
  );

  if (portalTarget) return createPortal(dialog, portalTarget);
  return dialog;
};

export default ChatbotCreateProductFromInvoice;
